import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLogo } from '../context/LogoContext';
import { useTheme } from '../context/ThemeContext';
import { Trophy, ArrowRight, Sun, Moon } from 'lucide-react';
import NotificationBell from './NotificationBell';

const MobileHeader = ({ title, showBack = false, actions, hideBell = false }) => {
  const { user, logout } = useAuth();
  const { logo, platformName } = useLogo();
  const { isDark, toggleThemeWithEffect } = useTheme();
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [visible, setVisible] = useState(true);
  const lastScrollY = useRef(0);

  useEffect(() => {
    const handleScroll = () => {
      const currentY = window.scrollY;
      setScrolled(currentY > 20);
      if (currentY > lastScrollY.current && currentY > 80) {
        setVisible(false);
      } else {
        setVisible(true);
      }
      lastScrollY.current = currentY;
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleLogout = () => { logout(); navigate('/auth'); };

  return (
    <div 
      className={`md:hidden sticky top-0 z-40 transition-all duration-300 ${
        visible ? 'translate-y-0' : '-translate-y-full'
      }`}
      style={{ 
        fontFamily: 'Tajawal, sans-serif',
        paddingTop: 'env(safe-area-inset-top, 0px)',
      }}
      data-testid="mobile-header"
    >
      {/* Glass background */}
      <div className={`absolute inset-0 transition-all duration-300 ${
        scrolled 
          ? 'backdrop-blur-xl border-b' 
          : 'backdrop-blur-md'
      }`} style={{
        background: scrolled
          ? isDark ? 'rgba(3,7,18,0.85)' : 'rgba(240,244,248,0.90)'
          : isDark ? 'rgba(3,7,18,0.45)' : 'rgba(240,244,248,0.60)',
        borderBottomColor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.07)',
      }} />
      
      <div className="relative flex justify-between items-center h-11 px-4">
        {/* Right side - Logo/Back */}
        <div className="flex items-center gap-2.5">
          {showBack ? (
            <button 
              onClick={() => navigate(-1)}
              className="flex items-center justify-center w-8 h-8 rounded-full transition-colors"
              style={{ background: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)' }}
              data-testid="header-back-btn"
            >
              <ArrowRight className="h-4 w-4" style={{ color: isDark ? '#ffffff' : '#0f172a' }} />
            </button>
          ) : (
            <Link to="/" className="flex items-center gap-2">
              <img
                src={logo || '/volta-logo-sm.webp'}
                alt="Logo"
                width="40"
                height="40"
                className="h-10 object-contain"
                style={{ width: 40, height: 40 }}
                loading="eager"
                fetchPriority="high"
                decoding="async"
              />
            </Link>
          )}
          <span className="text-sm font-bold tracking-tight" style={{ color: isDark ? '#ffffff' : '#0f172a' }}>
            {title || platformName}
          </span>
        </div>
        
        {/* Left side - Actions + Theme + Bell + User */}
        <div className="flex items-center gap-1">
          {actions}

          {/* Theme toggle — matches bell size/style exactly */}
          <button
            onClick={toggleThemeWithEffect}
            data-testid="theme-toggle-btn"
            className="flex items-center justify-center w-8 h-8 rounded-full transition-all duration-300 active:scale-90"
            style={{
              background: isDark ? 'rgba(83,195,174,0.09)' : 'rgba(83,195,174,0.10)',
              border: isDark ? '1px solid rgba(83,195,174,0.22)' : '1px solid rgba(83,195,174,0.30)',
            }}
            title={isDark ? 'الوضع النهاري' : 'الوضع الليلي'}
          >
            {isDark
              ? <Sun  className="w-[15px] h-[15px]" style={{ color: '#53C3AE' }} />
              : <Moon className="w-[15px] h-[15px]" style={{ color: '#53C3AE' }} />
            }
          </button>

          {/* Notification Bell — only on main pages */}
          {!hideBell && <NotificationBell className="!p-1.5" />}
        </div>
      </div>
    </div>
  );
};

export default MobileHeader;
